import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { UpsellModal } from './upsellModal.js';
import { UpsellOfferCard } from './UpsellOfferCard.js';
import { fetchUpsellChain, seedDeadline, } from './upsell.js';
/**
 * The PRE-purchase 1-Click Upsell chain, played in the CART journey (feature 0069 lot C).
 *
 * The same chain has always played on "Commander" in the COD order form. A shop that takes cards never sees
 * that form — it gets "Ajouter au panier" — so its seller configured offers no buyer could be shown. This
 * plays them at the equivalent moment: the click that commits to the product.
 *
 * **The product is never held hostage.** The caller adds the main line only AFTER this finishes, so the
 * sequence is: offers → accepted lines → main line (which opens the cart drawer, once, with everything in
 * it). Closing the window, a chain that never loads, a backend that is down: all end the same way, with the
 * product in the cart. An upsell is worth money; it is never worth the sale the shop already had.
 *
 * The chain is PREFETCHED on first intent (hovering or focusing the button), so the click opens straight
 * onto the offer instead of onto a spinner. If the buyer is faster than the network, the window shows the
 * loading line for a moment and then the offer — the same behaviour the COD form has always had.
 */
export function CartUpsellChain({ chain, onFinished, }) {
    const [index, setIndex] = useState(0);
    const [accepted, setAccepted] = useState([]);
    const [deadlines, setDeadlines] = useState({});
    // `onFinished` must fire once and only once: twice would add the accepted lines twice, and never would
    // leave the buyer's product out of the cart. A ref, because every exit path can race the others.
    const done = useRef(false);
    const offers = chain?.offers ?? [];
    const current = offers[index];
    const finish = (lines) => {
        if (done.current)
            return;
        done.current = true;
        onFinished(lines);
    };
    // The countdown starts when the offer is SEEN, never when it was fetched — the shared rule (`seedDeadline`).
    useEffect(() => {
        setDeadlines((d) => seedDeadline(d, current));
    }, [current]);
    // Nothing to play: hand back immediately so the caller writes the cart without a frame of delay.
    useEffect(() => {
        if (offers.length === 0)
            finish([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `finish` is guarded by its own ref
    }, [offers.length]);
    if (!current)
        return null;
    const next = (lines) => {
        if (index + 1 >= offers.length) {
            finish(lines);
            return;
        }
        setAccepted(lines);
        setIndex(index + 1);
    };
    return (_jsx(UpsellModal, { busy: false, onClose: () => finish(accepted), children: _jsx(UpsellOfferCard, { offer: current, index: index + 1, total: offers.length, deadline: deadlines[current.offerId] ?? null, onAccept: (line) => next([...accepted, line]), onReject: () => next(accepted), onSkipChain: () => finish(accepted) }, current.offerId) }));
}
/** Fetch the chain for a product ONCE, on first intent. Fail-open: any refusal answers null. */
export function prefetchCartChain(productId, store, fetchImpl) {
    return fetchUpsellChain(productId, {
        store,
        ...(fetchImpl ? { fetchImpl } : {}),
    });
}
//# sourceMappingURL=CartUpsellChain.js.map